/**
 * First-touch campaign attribution: the `utm_*` and ad click ids this browser first
 * landed with, which every lead form sends along with the DOT.
 *
 * The backend copies them onto the lead the first time its DOT is entered and never
 * overwrites them, so this keeps the earliest landing that carried any: a later visit
 * from a different campaign does not replace it.
 */

const STORAGE_KEY = 'cai_first_touch_attribution'

export const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'gclid',
  'fbclid',
] as const

type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]

export type Attribution = Partial<Record<AttributionKey, string>>

/** Keep the landing URL's campaign parameters, unless an earlier landing already left some. */
export function rememberAttribution(search: string = window.location.search) {
  if (Object.keys(storedAttribution()).length > 0) return

  const params = new URLSearchParams(search)
  const attribution: Attribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key)?.trim()
    if (value) attribution[key] = value
  }
  if (Object.keys(attribution).length === 0) return

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution))
  } catch {
    // Storage blocked (private mode, a strict extension): the lead just goes unattributed.
  }
}

/** The remembered campaign parameters, empty for a visitor who never landed with any. */
export function storedAttribution(): Attribution {
  let stored: unknown
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}')
  } catch {
    return {}
  }
  if (typeof stored !== 'object' || stored === null) return {}

  const attribution: Attribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const value = (stored as Record<string, unknown>)[key]
    if (typeof value === 'string' && value) attribution[key] = value
  }
  return attribution
}
